import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { X } from 'lucide-react';

import { RankedCandidate } from '../../../types/project.ts';
import { Norm } from '../../../types/user.ts';
import {
  ExpectedActualPair,
  getActualValueColor,
  getComparisonOperator,
  isTargetSatisfied,
  pairExpectedActualValues,
} from '../../../utils/normSatisfactionUtils.ts';
import { getNormName } from '../../../utils/pdfUtils';
import { getPhotometricSymbol } from '../../../utils/photometricSymbols.tsx';
import { getPhotometricUnit } from '../../../utils/unitSystem.ts';

interface MarketMatchResultsPreviewModalProps {
  isOpen: boolean;
  lampName: string;
  aggregateResults: NonNullable<RankedCandidate['aggregateResults']>;
  layerDisplayNames: Record<string, string>;
  norms: Norm[];
  onClose: () => void;
}

const calculateLayerSatisfaction = (
  layer: NonNullable<RankedCandidate['aggregateResults']>[number],
): { satisfied: number; total: number } => {
  const expectedValues = layer.expectedValues || [];
  const actualValues = layer.actualValues || [];
  const pairs = pairExpectedActualValues(expectedValues || [], actualValues || []);
  const satisfiedCount = pairs.filter((pair) => isTargetSatisfied(pair)).length;
  return { satisfied: satisfiedCount, total: pairs.length };
};

export function MarketMatchResultsPreviewModal({
  isOpen,
  lampName,
  aggregateResults,
  layerDisplayNames,
  norms,
  onClose,
}: MarketMatchResultsPreviewModalProps) {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogTitleId = 'market-match-results-preview-modal-title';

  useEffect(() => {
    if (isOpen) {
      overlayRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[1px] flex items-center justify-center p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      tabIndex={-1}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogTitleId}
        className="w-full max-w-[560px] max-h-[80dvh] overflow-hidden rounded-xl border border-stone-700 bg-stone-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-700">
          <div className="min-w-0">
            <p id={dialogTitleId} className="text-sm text-white font-semibold truncate">
              {lampName}
            </p>
            <p className="text-xs text-stone-400 truncate">
              {t('workspace.marketMatchPreview', 'Market match result preview')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-white transition-colors"
            aria-label={t('common.close', 'Close')}
          >
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(80dvh-64px)]">
          {!aggregateResults || aggregateResults.length === 0 ? (
            <div className="px-8 py-3 text-sm text-gray-300">{t('workspace.measurementNotAvailable')}</div>
          ) : (
            <div className="space-y-1.5 p-2">
              {aggregateResults.map((layer) => {
                const expectedValues = layer.expectedValues || [];
                const actualValues = layer.actualValues || [];
                const displayName = layerDisplayNames[layer.name] || layer.name;

                if (expectedValues.length === 0) return null;

                const pairs: ExpectedActualPair[] = pairExpectedActualValues(expectedValues, actualValues);

                const { satisfied, total } = calculateLayerSatisfaction(layer);
                const normName = layer.normId ? getNormName(layer.normId, norms) : '';

                return (
                  <div
                    key={layer.uuid}
                    className="rounded-lg font-inter overflow-hidden border border-neutral-700"
                    style={{
                      background: 'linear-gradient(170deg, rgba(79, 209, 46, 0.15) 0%, rgba(35, 35, 35, 0.95) 100%)',
                    }}
                  >
                    <div className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] min-w-0">
                      <span className="text-[13px] font-medium text-white truncate min-w-0" title={displayName}>
                        {displayName}
                      </span>
                      <div className="flex items-center gap-1 ml-auto flex-shrink-0">
                        {normName && (
                          <>
                            <span className="text-gray-300">{t('workspace.norm', 'Norm')}:</span>
                            <span className="text-gray-200 truncate max-w-[80px]" title={normName}>
                              {normName}
                            </span>
                            <span className="text-gray-500">,</span>
                          </>
                        )}
                        <span className="text-gray-300">{t('workspace.satisfied', 'Satisfied')}:</span>
                        <span className="whitespace-nowrap">
                          <span className="text-lime-400 font-semibold">{satisfied}</span>
                          <span className="text-gray-300">/{total}</span>
                        </span>
                      </div>
                    </div>

                    <div className="px-2.5 pb-2">
                      <div className="grid grid-cols-12 items-center text-[9px] text-gray-300 uppercase tracking-wide mb-0.5">
                        <div className="col-span-1" />
                        <div className="col-span-3" />
                        <div className="col-span-4">{t('workspace.value', 'Value')}</div>
                        <div className="col-span-4">{t('workspace.targetValue', 'Required')}</div>
                      </div>

                      {pairs.map((pair) => {
                        const uom = getPhotometricUnit(pair.function);
                        const isSatisfied = isTargetSatisfied(pair);
                        const operator = getComparisonOperator(pair.type);

                        return (
                          <div
                            key={`${pair.function}-${pair.type}`}
                            className="grid grid-cols-12 items-center text-[11px] h-[20px] border-b border-white/5"
                          >
                            <div className="col-span-1 flex justify-center">
                              <div
                                className={`w-2 h-2 rounded-full ${isSatisfied ? 'bg-lime-400' : 'bg-red-400'}`}
                                title={isSatisfied ? t('workspace.targetMet') : t('workspace.targetNotMet')}
                              />
                            </div>
                            <div className="col-span-3 text-gray-300">
                              {getPhotometricSymbol(pair.function, pair.type)}
                            </div>
                            <div className={`col-span-4 ${getActualValueColor(pair)} font-medium`}>
                              {pair.actual?.toFixed(2)}
                              {uom ? ` ${uom}` : ''}
                            </div>
                            <div className="col-span-4 text-gray-300">
                              {operator} {pair.expected?.toFixed(2)}
                              {uom ? ` ${uom}` : ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
